import { Suspense, lazy, useEffect, useState } from 'react';
import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import emailjs from '@emailjs/browser';
import { Toaster } from 'react-hot-toast';
import { Helmet } from 'react-helmet-async';
import { AnimatePresence } from 'framer-motion';

// Context and theme providers
import { ThemeProvider } from './context/ThemeContext';

// Core layout components
import { TechBackground } from './components/TechBackground';
import { CursorEffects } from './components/CursorEffects';
import { ScrollProgress } from './components/ScrollProgress';
import { Navbar } from './components/Navbar';
import { LoadingPage } from './components/LoadingPage';
import { Hero } from './components/Hero';

/**
 * Lazy-loaded page sections for optimal performance
 * Below-the-fold sections are loaded dynamically
 */
const About = lazy(() => import('./components/About').then(m => ({ default: m.About })));
const Projects = lazy(() => import('./components/Projects').then(m => ({ default: m.Projects })));
const Skills = lazy(() => import('./components/Skills').then(m => ({ default: m.Skills })));
const Contact = lazy(() => import('./components/Contact').then(m => ({ default: m.Contact })));
const Footer = lazy(() => import('./components/Footer').then(m => ({ default: m.Footer })));

/**
 * Main application component that handles:
 * - Smooth scrolling setup with Lenis
 * - GSAP ScrollTrigger configuration
 * - Layout structure and component organization
 * - Seamless window shutter loading sequence
 */
function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize EmailJS with your public key
    emailjs.init("IL1KozmAvEZ9mSims");
    
    // Initialize GSAP plugins for scroll-based animations
    gsap.registerPlugin(ScrollTrigger);

    // Configure Lenis for smooth scrolling behavior
    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      touchMultiplier: 1.8,
    });

    // Expose lenis instance globally for programmatic navigation
    window.lenis = lenis;

    // Synchronize ScrollTrigger with Lenis scroll events
    lenis.on('scroll', ScrollTrigger.update);

    // Coordinate animation frames with GSAP's ticker
    const handleTick = (time: number) => {
      lenis.raf(time * 1000);
    };
    
    gsap.ticker.add(handleTick);
    gsap.ticker.lagSmoothing(0);

    // Cleanup function
    return () => {
      gsap.ticker.remove(handleTick);
      lenis.destroy();
      delete window.lenis;
      
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
      ScrollTrigger.clearMatchMedia();
    };
  }, []);

  return (
    <ThemeProvider>
      <Helmet>
        <title>Nugitha Disas | Software Engineer &amp; Full-Stack Developer</title>
        <link rel="canonical" href="https://nugi-dev.vercel.app/" />
        <meta name="description" content="Nugitha Disas is a Software Engineering undergraduate and Full-Stack Developer specializing in Java, Spring Boot, React, TypeScript, and modern web application development." />
        <meta name="keywords" content="Nugitha Disas, Nugi, Nugi.dev, Software Engineer, Full-Stack Developer, Java Developer, Spring Boot, React, TypeScript, Web Developer Portfolio" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nugi-dev.vercel.app/" />
        <meta property="og:title" content="Nugitha Disas | Software Engineer &amp; Full-Stack Developer" />
        <meta property="og:description" content="Explore the portfolio of Nugitha Disas, a Software Engineering undergraduate and Full-Stack Developer building modern web applications with Java, Spring Boot, React, TypeScript, and more." />
        <meta property="og:image" content="https://nugi-dev.vercel.app/assets/profile_picture.webp" />
        <meta property="og:image:alt" content="Nugitha Disas - Software Engineer and Full-Stack Developer" />
        <meta property="og:site_name" content="Nugitha Disas Portfolio" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://nugi-dev.vercel.app/" />
        <meta name="twitter:title" content="Nugitha Disas | Software Engineer &amp; Full-Stack Developer" />
        <meta name="twitter:description" content="Portfolio of Nugitha Disas, a Software Engineering undergraduate and Full-Stack Developer specializing in Java, Spring Boot, React, and TypeScript." />
        <meta name="twitter:image" content="https://nugi-dev.vercel.app/assets/profile_picture.webp" />
        <meta name="twitter:image:alt" content="Nugitha Disas - Software Engineer and Full-Stack Developer" />
      </Helmet>

      {/* Shutter Window Loading Page Overlay */}
      <AnimatePresence mode="wait">
        {isLoading && (
          <LoadingPage onComplete={() => setIsLoading(false)} />
        )}
      </AnimatePresence>

      {/* Skip navigation link for accessibility */}
      <a 
        href="#main" 
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 bg-brand-purple text-white px-4 py-2 rounded-md z-[100]"
      >
        Skip to content
      </a>
      
      {/* Main application container with responsive background gradients */}
      <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-gradient-to-br from-slate-50 via-brand-light/30 to-brand-purple/20 dark:from-brand-dark-bg dark:via-brand-dark-bg-secondary dark:to-brand-dark-bg text-brand-navy dark:text-brand-dark-text-secondary transition-colors duration-500">
        {/* Global UI components */}
        <ScrollProgress />
        <TechBackground />
        <CursorEffects />
        <Navbar />
        
        {/* Main content area */}
        <main id="main" className="relative z-10 pt-16 sm:pt-20 w-full max-w-full overflow-x-hidden">
          {/* Eagerly rendered Hero for immediate above-the-fold presence */}
          <Hero />

          {/* Unified Suspense wrapper for all remaining sections including footer */}
          <Suspense fallback={null}>
            <About />
            <Projects />
            <Skills />
            <Contact />
            <Footer />
          </Suspense>
        </main>
        
        {/* Toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: 'var(--toast-bg)',
              color: 'var(--toast-color)',
              borderRadius: '12px',
              padding: '16px',
              fontSize: '14px',
              fontWeight: '500',
            },
            success: {
              style: {
                border: '1px solid #10b981',
              },
            },
            error: {
              style: {
                border: '1px solid #ef4444',
              },
            },
          }}
        />
      </div>
    </ThemeProvider>
  );
}

export default App;
